<template lang="pug">
#family-list(v-cloak)
  .box
    .box-header
      .d-flex.flex-row
        .d-flex.flex-column.flex-fill
          .title
            span 家族ユーザー
          .note
            span ご家族には「ID通知書」と、「サービスご紹介」「ユーザー登録方法」をお配りください。
            br
            span ご家族がLINEからユーザー登録すると一覧に表示されます。ご家族は何人でも登録できます。
            br
            span スマートフォンをお持ちでないご家族は「メール利用の家族を追加」からメールアドレスを登録できます。
        .buttons.d-flex.flex-row-reverse.justify-content-end.flex-wrap
          button.btn.add-btn(@click="gotoFamilyRegister()" type="button") メール利用の家族を追加
    .box-body(style="padding-top:0;")
      .d-flex.justify-content-end
        .refresh-icon(@click.stop="refresh" title="再読み込みします")
      .mb-2
      FamilyTable(
        v-model="items"
        :fields="fields"
        :totalRows.sync="rows"
        :currentPage.sync="currentPage"
        :perPage="perPage"
        :isLoading="isLoading"
        :filter="filter"
        @onEdit="gotoFamilyEdit"
        @onLineDelete="showConfirmFamilyDeleteModal"
        @onMailDelete="showConfirmMailFamilyDeleteModal"
        @onSendMessage="showTestMailModal"
      )
      .table-options.d-flex.flex-row.justify-content-end.align-items-center.flex-wrap
        //- CSVダウンロード
        button.btn.download-btn.mr-2(:class="{ 'disable': isLoading }" :disabled="isLoading" @click="onCsvDownload") ダウンロード
        //- 検索
        .search-filter.d-flex.mr-1.align-items-center.mr-2
          b-input-group.search-filter-input-group.mr-1(size="md")
            b-input-group-prepend
              b-input-group-text.search-filter-icon
                .search-icon
            b-form-input.search-filter-input(v-model="filter.searchWord" type="search" placeholder="検索ワードを入力してください")
          HelpIcon.d-flex.justify-content-end.align-items-center(popoverId="search-filter-help" title="検索項目について")
            template(v-slot:text)
              div 検索項目は以下になります。
              div ・利用者名
              div ・利用者名（ふりがな）
              div ・部屋名
              div ・家族ユーザー名
              div ・続柄
              div ・役割
        //- フィルタリング
        span.table-option-text 利用者グループ:
        .filter
          b-form-select(
            id="filter-input"
            v-model="filter.id"
            :options="filterOptions"
            size="md"
          )
        //- ページあたりの行数
        span.table-option-text 1ページの行数:
        .per-page
          b-form-select(
            id="per-page-select"
            v-model="perPage"
            :options="pageOptions"
            size="md"
            @change="selectPerPage"
          )
        //- ページネーション
        b-pagination.m-0(
          v-model="currentPage"
          :total-rows="rows"
          :per-page="perPage"
          :disabled="isLoading"
          @change="selectPage"
          hide-goto-end-buttons
        )

  ResidentCheckDeleteModal(v-model="showFamilyDeleteModal"
    :title="deleteMessage" :sendMessage.sync="sendDeleteMessage" :deleteMessageTitle.sync="deleteModalMessageTitle" :deleteMessageText.sync="deleteModalMessageText"
    @ok="deleteFamily()")
    template(#message)
      .message 削除前に以下をご確認ください。
      ul.confirm-list
        li 削除したご家族はHitomeQコネクトの機能を使用できなくなります。
        li 削除後にご家族が再使用する場合は、再度ご家族にユーザー登録していただく必要があります。
  CheckDeleteModal(v-model="showDeleteMailFamilyModal" :title="deleteMessage" @ok="deleteMailFamily()")
    template(#message)
      .message 削除前に以下をご確認ください。
      ul.confirm-list
        li 削除したご家族はHitomeQコネクトからのメールを受信できなくなります。
        li 削除後に再使用する場合は、再度ユーザー登録していただく必要があります。
  OkModal(v-model="showDeleteSuccessModal" title="削除に成功しました。")

  TestMailModal(v-model="testMailModalShow" :target.sync="testMailFamilyTargetName" :subject.sync="testMailSubject" :text.sync="testMailText"
    :targetType="1" :showValidationAlert.sync="showValidationAlert" @sendMail="confirmSendMail")
  TestMailConfirmModal(v-model="testMailConfirmModalShow" :target="testMailFamilyTargetName" :subject="testMailSubject" :text="testMailText" @sendMail="sendMail")
  OkModal(v-model="showSendMailSuccessModal" title="メールを送信しました。")

  PageLoading(:isBusy="isBusy" :message="loadingMessage")
</template>

<script lang="ts">
import { Component, Inject, Watch, Vue } from "vue-property-decorator";
import { userStore, itemListFilterStore, needRefreshesStore } from "@/store/store";
import { SendMessagesState } from "@/store/NeedRefreshes";
import { errorHandle } from "@/errorHandler";

import Setting from "../../../../public/setting.json";
import { PcUrl } from "@/commons/constants/PcUrl";
import FamiliesService, { FamilyDeleteModel, FamilyGetModel, SenderType } from "@/commons/services/Families";
import MailFamiliesService from "@/commons/services/MailFamilies";
import SendMailMessagesService, { SendMailMessagesPostModel } from "@/commons/services/SendMailMessages";
import ResidentGroupsService, { ResidentGroupsGetModel } from "@/commons/services/ResidentGroups";
import SystemError from "@/commons/models/SystemError";

import OkCancelModal from "@/pc/basics/OkCancelModal.vue";
import OkModal from "@/pc/basics/OkModal.vue";
import HelpIcon from "@/pc/basics/HelpIcon.vue";
import PageLoading from "@/pc/basics/PageLoading.vue";
import CheckDeleteModal from "@/pc/components/modals/CheckDeleteModal.vue";
import ResidentCheckDeleteModal from "@/pc/components/residents/ResidentCheckDeleteModal.vue";
import TestMailModal from "@/pc/components/modals/TestMailModal.vue";
import TestMailConfirmModal from "@/pc/components/modals/TestMailConfirmModal.vue";
import FamilyTable, { FamilyTableField, FamilyTableItem, FilterSetting } from "@/pc/containers/families/FamilyTable.vue";

/**
 * 家族一覧画面コンポーネント
 */
@Component({
  components: {
    PageLoading,
    OkCancelModal,
    OkModal,
    HelpIcon,
    CheckDeleteModal,
    ResidentCheckDeleteModal,
    TestMailModal,
    TestMailConfirmModal,
    FamilyTable
  },
  name: "FamilyList",
  $_veeValidate: { validator: "new" }
})
export default class FamilyList extends Vue {
  /** 家族ユーザーサービス */
  @Inject("FamiliesService") familiesService!: FamiliesService;
  /** 家族メールユーザーサービス */
  @Inject("MailFamiliesService") mailFamiliesService!: MailFamiliesService;
  /** メール送信サービス */
  @Inject("SendMailMessagesService") sendMailMessagesService!: SendMailMessagesService;
  /** 利用者グループサービス */
  @Inject("ResidentGroupsService") residentGroupsService!: ResidentGroupsService;

  /** ページ全体ローディング表示可否 */
  isBusy: boolean = false;
  /** ローディングメッセージ */
  loadingMessage: string = "";
  /** 家族削除モーダル表示可否 */
  showFamilyDeleteModal: boolean = false;
  /** 削除確認モーダル表示可否 */
  showDeleteMailFamilyModal: boolean = false;
  /** 削除完了モーダル表示可否 */
  showDeleteSuccessModal: boolean = false;
  /** テストメール送信完了モーダル表示可否 */
  showSendMailSuccessModal: boolean = false;

  /** バリデーションアラート表示可否 */
  showValidationAlert: boolean = false;

  /** 家族列名 */
  fields: Array<FamilyTableField> = [];
  /** 家族各行の情報 */
  items: Array<FamilyTableItem> = [];
  /** 読込み中か否か */
  isLoading: boolean = true;
  /** グループIDとグループ名のMAP */
  groupNameMap: Map<number, string> = new Map();

  /** 削除対象の利用者ID */
  deleteTargetResidentId: number = 0;
  /** 削除対象の家族ID */
  deleteTargetFamilyId: number = 0;
  /** 削除モーダルに表示するメッセージ */
  deleteMessage: string = "";
  /** 削除メッセージの送信可否 */
  sendDeleteMessage: boolean | null = null;
  /** 削除メッセージタイトル */
  deleteModalMessageTitle: string = "";
  /** 削除メッセージ本文 */
  deleteModalMessageText: string = "";

  /** テストメールモーダルの表示状態 */
  testMailModalShow: boolean = false;
  /** テストメール宛先 */
  testMailTargetAddress: string = "";
  /** テストメール宛先ソートキー */
  testMailTargetSortKey: string = "";
  /** テストメール宛先家族名(実際には{部屋名}・{利用者名}様 のご家族 {家族名}（{続柄}）様と表示する) */
  testMailFamilyTargetName: string = "";
  /** テストメール宛先家族名 */
  testMailFamilyName: string = "";
  /** テストメール宛先利用者部屋名 */
  testMailResidentRoom: string = "";
  /** テストメール宛先利用者名 */
  testMailResidentName: string = "";
  /** テストメールタイトル */
  testMailSubject: string = "";
  /** テストメール本文 */
  testMailText: string = "";

  /** テストメール確認モーダルの表示状態 */
  testMailConfirmModalShow: boolean = false;

  /** フィルタリング条件 */
  filter: FilterSetting = { searchWord: "", id: "" };
  /** フィルター選択項目 */
  filterOptions: Array<{ value: string; text: string }> = [];
  /** ページングの現在ページ番号 */
  currentPage: number = 1;
  /** ページングの全体行数 */
  rows: number = 0;
  /** 1ページに表示する行数 */
  perPage: number = Number(Setting.family.perPage);
  /** 1ページに表示する行数の選択項目 */
  pageOptions: number[] = Setting.family.pageOptions;

  /**
   * ### 1ページあたりの表示行数の変更イベント
   *
   * - 変更をクッキーに持たせる
   */
  @Watch("perPage")
  public changePerPage(perPage: number): void {
    this.setCookie(perPage);
    return;
  }

  /**
   * ### 1ページあたりの表示行数をCookieに設定する
   */
  private setCookie(perPage: number): void {
    // Cookie有効秒数。有効期限は必ず設定しないといけないので、10年分にしておく
    const saveSecond = 10 * 365 * 24 * 60 * 60;
    document.cookie = `family-list-per-page=${perPage}; max-age=${saveSecond};`;
    return;
  }

  @Watch("sendDeleteMessage")
  changeSendDeleteMessage(sendDeleteMessage: boolean): void {
    this.setCookieForSendDeleteMessage(sendDeleteMessage);
    return;
  }

  /**
   * ### 1ページあたりの表示行数をCookieに設定する
   */
  private setCookieForSendDeleteMessage(sendDeleteMessage: boolean): void {
    // Cookie有効秒数。有効期限は必ず設定しないといけないので、10年分にしておく
    const saveSecond = 10 * 365 * 24 * 60 * 60;
    document.cookie = `family-list-send-delete-message=${sendDeleteMessage}; max-age=${saveSecond};`;
    return;
  }

  /**
   * ### vue lifecycle mounted
   */
  public async mounted(): Promise<void> {
    // 画面初期表示時に作成して変更しないものを初期化する
    const fields: Array<FamilyTableField> = this.createFields();
    this.fields = fields;
    // 1ページあたりの表示行数はCookieから取得する。取得できない場合はデフォルト値を使用する
    const perPage: number | null = this.loadCookie();
    this.perPage = perPage ? perPage : Number(Setting.family.perPage);
    // 削除時のメッセージ送信可否はCookieから取得する。取得出来ない場合はnull（未入力）を使用する
    const sendDeleteMessage: boolean | null = this.loadCookieForSendDeleteMessage();
    this.sendDeleteMessage = sendDeleteMessage != null ? sendDeleteMessage : null;
    // フィルタの復元
    this.restoreFilterSetting();

    await this.initialize();

    return;
  }

  /**
   * ### 画面初期化
   */
  public async initialize(): Promise<void> {
    this.isLoading = true;

    this.items = [];
    this.currentPage = 1;

    await this.createView();

    this.isLoading = false;

    return;
  }

  /**
   * ### 家族の列名を作成する
   */
  public createFields(): Array<FamilyTableField> {
    const fields: Array<FamilyTableField> = [];
    fields.push({ key: "senderType", label: "送信手段", sortable: true, sortDirection: "desc", class: "sender-type text-center" });
    fields.push({ key: "room", label: "部屋名", sortable: true, sortDirection: "desc", class: "room" });
    fields.push({ key: "residentName", label: "利用者名", sortable: true, sortDirection: "desc", class: "resident-name" });
    fields.push({ key: "familyName", label: "家族ユーザー名", sortable: true, sortDirection: "desc", class: "family-name" });
    fields.push({ key: "familyRelation", label: "続柄", sortable: false, sortDirection: "desc", class: "relation" });
    fields.push({ key: "familyPosition", label: "役割", sortable: false, sortDirection: "desc", class: "position" });
    fields.push({ key: "familyReplyFunction", label: "家族からの連絡", sortable: false, sortDirection: "desc", class: "family-reply-function" });
    fields.push({ key: "familyPictureFunction", label: "写真印刷依頼", sortable: false, sortDirection: "desc", class: "family-picture-function" });
    fields.push({ key: "operation", label: "登録情報", sortable: false, sortDirection: "desc", class: "operation text-center" });
    fields.push({ key: "sendMessage", label: "登録確認", sortable: false, sortDirection: "desc", class: "send-message text-center" });
    return fields;
  }

  /**
   * ### Cookieを見て、値が保持されている場合はCookieの値を返す
   */
  private loadCookie(): number | null {
    if (document.cookie) {
      let cookieMap: any = {};
      let cookies: Array<string> = document.cookie.split("; ");
      cookies.forEach(function (value) {
        //cookie名と値に分ける
        let content = value.split("=");
        cookieMap[content[0]] = content[1];
      });

      // cookieに値が格納されていないとundefinedが格納されているが、実際にはゴミデータが格納されているのでisNaN()でチェックが必要
      const perPage: number = cookieMap["family-list-per-page"];

      return isNaN(perPage) ? null : perPage;
    }

    return null;
  }

  private loadCookieForSendDeleteMessage(): boolean | null {
    if (document.cookie) {
      let cookieMap: any = {};
      let cookies: Array<string> = document.cookie.split("; ");
      cookies.forEach(function (value) {
        //cookie名と値に分ける
        let content = value.split("=");
        cookieMap[content[0]] = content[1];
      });

      const sendDeleteMessage: string = cookieMap["family-list-send-delete-message"];
      if (sendDeleteMessage == "true") {
        return true;
      } else if (sendDeleteMessage == "false") {
        return false;
      } else {
        return null;
      }
    }

    return null;
  }

  /** フィルタ情報がstoreに存在する場合は復元する */
  private restoreFilterSetting(): void {
    if (this.$options.name && itemListFilterStore.filterMap[this.$options.name] != undefined) {
      this.filter = itemListFilterStore.filterMap[this.$options.name];
    }
    return;
  }

  /**
   * ### 全ての利用者グループを取得する
   */
  public async getResidentGroups(): Promise<Array<ResidentGroupsGetModel>> {
    const response: Array<ResidentGroupsGetModel> = await this.residentGroupsService.gets(userStore);
    return response;
  }

  /**
   * ### グループIDとグループ名のMAPを作成する
   */
  public createGroupNameMap(residentGroups: Array<ResidentGroupsGetModel>): Map<number, string> {
    let groupMap: Map<number, string> = new Map<number, string>();
    for (const group of residentGroups) {
      groupMap.set(group.id, group.name);
    }
    return groupMap;
  }

  /**
   * ### 利用者グループフィルター用選択項目
   *
   * - 検索用に利用者グループマスターのMAPを作る
   * - アカウント情報から許可されている利用者グループIDの配列を取得する
   * - 利用者グループマスターと照合して選択項目の利用者グループを作成する
   */
  public createFilterOptions(residentGroups: Array<ResidentGroupsGetModel>): Array<{ value: string; text: string }> {
    const filterOptions: Array<{ value: string; text: string }> = [];

    filterOptions.push({
      value: "",
      text: "全て"
    });

    const allowedGroupsMap: Map<number, number> = new Map<number, number>();
    for (const allowedGroup of userStore.loginUser.permissions.residentGroups) {
      allowedGroupsMap.set(allowedGroup, allowedGroup);
    }
    for (const group of residentGroups) {
      if (allowedGroupsMap.has(group.id)) {
        filterOptions.push({
          value: `${group.id}`,
          text: group.name
        });
      }
    }

    return filterOptions;
  }

  /**
   * ### 画面情報作成
   */
  public async createView(): Promise<void> {
    // グループIDとグループ名の変換用にグループ情報のMAPを作る
    const groups: Array<ResidentGroupsGetModel> = await this.getResidentGroups();
    const groupNameMap: Map<number, string> = this.createGroupNameMap(groups);
    this.groupNameMap = groupNameMap;

    const filterOptions: Array<{ value: string; text: string }> = await this.createFilterOptions(groups);
    this.filterOptions = filterOptions;

    // 全体の行数を非同期で取得する
    this.setRows();

    // 家族テーブルに渡す各プロパティを作成する
    const families: Array<FamilyGetModel> = await this.getFamilies();
    const items: Array<FamilyTableItem> = this.createItems(families, this.groupNameMap);
    this.items = items;

    return;
  }

  /**
   * ### ページング用の全件数を設定する
   */
  public async setRows() {
    try {
      const count: number = await this.familiesService.getCountInFacility(userStore);
      this.rows = count;
    } catch (error) {
      // 本処理はawaitされない非同期処理なので、共通エラーハンドラーが例外をキャッチしない
      // 例外発生時は自分でエラーハンドラーを呼ぶ
      errorHandle(error, this);
    }

    return;
  }

  /**
   * ### 家族を取得する
   *
   * - 家族は取得制限をしないためstartIndex、endIndexを指定しない
   */
  public async getFamilies(): Promise<Array<FamilyGetModel>> {
    let response: Array<FamilyGetModel> = [];

    try {
      response = await this.familiesService.getsInFacility(userStore);
    } catch (error) {
      const systemerror: SystemError = error as SystemError;
      // 413エラーの場合、全件取得した結果がペイロード制限に引っかかったことになるので、index指定で段階的に取得する
      if (systemerror.statusCode == 413) {
        const pagePerCount: number = 50;
        const count: number = await this.familiesService.getCountInFacility(userStore);
        const loopEnd: number = Math.ceil(count / pagePerCount);

        for (let i = 0; i < loopEnd; i++) {
          const params: any = {
            startIndex: i * pagePerCount,
            endIndex: i * pagePerCount + pagePerCount
          };
          response = response.concat(await this.familiesService.getsInFacility(userStore, params));
        }
      }
    }
    return response;
  }

  /**
   * ### 家族の行を作成する
   */
  public createItems(families: Array<FamilyGetModel>, groupNameMap: Map<number, string>): Array<FamilyTableItem> {
    let items: Array<FamilyTableItem> = [];

    for (const family of families) {
      const groupName: string | undefined = groupNameMap.get(family.groupId);

      items.push({
        id: family.id,
        residentsSortKey: family.residentsSortKey,
        groupId: family.groupId,
        groupName: groupName ? groupName : "",
        room: family.room,
        residentName: family.name,
        residentKanaName: family.kanaName,
        familyId: family.familyId,
        familyName: family.familyName,
        familyRelation: family.familyRelation,
        familyPosition: family.familyPosition,
        familyMailaddress: family.familyMailaddress ? family.familyMailaddress : "",
        familyReplyFunction: family.familyPermittedFunctions.opinion,
        familyPictureFunction: family.familyPermittedFunctions.picture,
        senderType: family.senderType,
        updated: family.updated
      });
    }

    return items;
  }

  /**
   * ### vue lifecycle activated
   */
  public async activated(): Promise<void> {
    this.$validator.errors.clear();
    if (needRefreshesStore.familyList.familyList == true) {
      await this.createView();
      needRefreshesStore.familyList.familyList = false;
    }
    return;
  }

  /**
   * ### 1ページあたりの表示行数変更イベント
   *
   * - 1ページあたりの表示行数を更新
   */
  public async selectPerPage(perPage: number): Promise<void> {
    this.perPage = perPage;
    this.currentPage = 1;
    return;
  }

  /**
   * ### ページネーションのページ番号押下
   */
  public async selectPage(page: number): Promise<void> {
    this.currentPage = page;
    return;
  }

  /**
   * ### 家族メールユーザー登録画面へ遷移する
   */
  public gotoFamilyRegister(): void {
    this.$router.push(PcUrl.FamilyRegister);
    return;
  }

  /**
   * ### 家族メールユーザー編集画面へ遷移
   */
  public gotoFamilyEdit(family: FamilyTableItem): void {
    this.$router.push({ name: PcUrl.FamilyEdit, params: { residentid: `${family.id}`, id: `${family.familyId}` } });
    return;
  }

  /**
   * ### 家族ユーザー削除確認モーダルを表示する
   *
   * - 削除メッセージを動的に作成する
   */
  public showConfirmFamilyDeleteModal(family: FamilyTableItem): void {
    this.deleteModalMessageTitle = "ユーザー登録が削除されました";
    const deleteMessages = [];
    deleteMessages.push(`ご利用の施設から${family.familyName}様の登録情報が削除されましたので、ユーザー登録を削除させていただきました。`);
    deleteMessages.push("今までHitomeQコネクトをご愛顧いただきありがとうございました。");
    this.deleteModalMessageText = deleteMessages.join("\n");

    const room: string = family.room ? family.room : "";
    const residentName: string = family.residentName ? family.residentName : "";
    const familyName: string = family.familyName ? family.familyName : "";
    const familyRelation: string = family.familyRelation ? `（${family.familyRelation}）` : "";
    this.deleteMessage = `${room}・${residentName}様のご家族${familyName}${familyRelation}様を削除しますか？`;
    this.deleteTargetResidentId = family.id;
    this.deleteTargetFamilyId = family.familyId;

    this.showFamilyDeleteModal = true;

    return;
  }

  /**
   * ### 家族ユーザーを削除する
   */
  public async deleteFamily(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "削除中...";
    this.showFamilyDeleteModal = false;

    const requestBody: FamilyDeleteModel = {
      deleteMessageTitle: this.sendDeleteMessage ? this.deleteModalMessageTitle : "",
      deleteMessageText: this.sendDeleteMessage ? this.deleteModalMessageText : ""
    };
    await this.familiesService.delete(userStore, this.deleteTargetResidentId, this.deleteTargetFamilyId, requestBody);

    // itemsから削除した家族を削除するだけで再取得リクエストはしない
    this.items = this.items.concat().filter((item: FamilyTableItem) => {
      if (item.id == this.deleteTargetResidentId && item.familyId == this.deleteTargetFamilyId) {
        return false;
      } else {
        return true;
      }
    });
    this.rows--;
    // メッセージ送信の宛先が変更になるので再読み込みさせるようstoreを更新
    let sendMessageState: SendMessagesState = needRefreshesStore.sendMessage;
    sendMessageState.sendMessages = true;
    needRefreshesStore.setSendMessage(sendMessageState);

    this.showDeleteSuccessModal = true;
    this.isBusy = false;

    return;
  }

  /**
   * ### 家族メールユーザー削除確認モーダルを表示する
   */
  public showConfirmMailFamilyDeleteModal(family: FamilyTableItem): void {
    // 削除メッセージを動的に作成する
    const room: string = family.room ? family.room : "";
    const residentName: string = family.residentName ? family.residentName : "";
    const familyName: string = family.familyName ? family.familyName : "";
    const familyRelation: string = family.familyRelation ? `（${family.familyRelation}）` : "";
    this.deleteMessage = `${room} ${residentName} 様のご家族 ${familyName}${familyRelation}様を削除しますか？`;
    this.showDeleteMailFamilyModal = true;

    this.deleteTargetResidentId = family.id;
    this.deleteTargetFamilyId = family.familyId;

    return;
  }

  /**
   * ### 家族メールユーザーを削除する
   */
  public async deleteMailFamily(): Promise<void> {
    this.isBusy = true;
    this.loadingMessage = "削除中...";

    await this.mailFamiliesService.delete(userStore, this.deleteTargetResidentId, this.deleteTargetFamilyId);

    // itemsから削除した家族を削除するだけで再取得リクエストはしない
    this.items = this.items.concat().filter((item: FamilyTableItem) => {
      if (item.id == this.deleteTargetResidentId && item.familyId == this.deleteTargetFamilyId) {
        return false;
      } else {
        return true;
      }
    });
    this.rows--;
    // メッセージ送信の宛先が変更になるので再読み込みさせるようstoreを更新
    let sendMessageState: SendMessagesState = needRefreshesStore.sendMessage;
    sendMessageState.sendMessages = true;
    needRefreshesStore.setSendMessage(sendMessageState);

    // 削除成功ダイアログを表示する
    this.showDeleteSuccessModal = true;
    this.isBusy = false;

    return;
  }

  /**
   * ### テストメール送信モーダルを表示する
   *
   * - テストメール送信モーダルに必要な情報を作成する
   * - モーダルを表示する
   */
  public showTestMailModal(family: FamilyTableItem): void {
    this.testMailTargetAddress = family.familyMailaddress ? family.familyMailaddress : "";
    this.testMailTargetSortKey = family.residentsSortKey;
    this.testMailFamilyTargetName = `${family.room}・${family.residentName}様 のご家族 ${family.familyName}（${family.familyRelation}）様`;
    this.testMailFamilyName = `${family.familyName}`;
    this.testMailResidentRoom = `${family.room}`;
    this.testMailResidentName = `${family.residentName}`;
    this.testMailSubject = "HitomeQコネクト登録確認メール";
    this.testMailText =
      "HitomeQコネクトのメール配信サービスへのお申し込みありがとうございます。\n" +
      "お申し込みのメールアドレスを登録致しました。\n" +
      "後ほど受信確認のご連絡をさせていただきます。\n\n" +
      "もしお心当たりの無い場合は、本メールを削除してください。";

    this.testMailModalShow = true;

    return;
  }

  /**
   * ### テストメール送信確認モーダルを表示する
   *
   * - テストメールの内容をバリデーションする
   * - バリデーションで問題が無ければテストメール確認モーダルを表示する
   */
  public async confirmSendMail(): Promise<void> {
    const validationResult: boolean = await this.$validator.validateAll();
    if (validationResult) {
      this.showValidationAlert = false;
      this.testMailConfirmModalShow = true;
    } else {
      this.showValidationAlert = true;
    }
    return;
  }

  /**
   * ### テストメールを送信する
   *
   * - メールを送信するのでテストメールモーダル、テストメール確認モーダルを非表示にする
   * - メール送信用のパラメータを作成する
   * - メールを送信する
   */
  public async sendMail(): Promise<void> {
    this.testMailModalShow = false;
    this.testMailConfirmModalShow = false;

    this.isBusy = true;
    this.loadingMessage = "送信中...";

    const sendMailMessage: SendMailMessagesPostModel = this.createSendMailMessageModel();
    await this.sendMailMessagesService.post(userStore, sendMailMessage);

    // 送信成功ダイアログを表示する
    this.showSendMailSuccessModal = true;
    this.isBusy = false;

    return;
  }

  /**
   * ### メール送信用パラメータを作成する
   */
  public createSendMailMessageModel(): SendMailMessagesPostModel {
    const sendMailMessage: SendMailMessagesPostModel = {
      sortKeys: [this.testMailTargetSortKey],
      targetName: this.testMailFamilyName,
      subject: this.$stringUtil.convertYen(this.testMailSubject),
      text: this.$stringUtil.convertYen(this.testMailText),
      writerId: userStore.loginUser.userId,
      writtenBy: userStore.loginUser.name,
      // メッセージ送信画面でしか使用しないので以下は空相当の情報を格納する
      type: "",
      addOptionText: false,
      addReadButton: false,
      addReplyButton: false,
      fileUrls: [],
      fileNames: [],
      addReceivedMessageUrl: false,
      needDbRegister: false
    };

    return sendMailMessage;
  }

  /**
   * ### 利用者一覧をCSVダウンロードする
   */
  public onCsvDownload(): void {
    const csv: string = this.createCsv(this.items);
    const fileName: string = this.createCsvFileName();
    this.downloadCsv(csv, fileName);
    return;
  }

  /**
   * ### 利用者一覧CSVを作成する
   *
   * - 1行目の先頭はexcel等での文字化け対策でBOMを付与する
   * - 1行目は項目名を作る
   * - 2行目以降は行情報にあたる情報を作成して出力する
   */
  public createCsv(items: Array<FamilyTableItem>): string {
    const csv: string[] = [];
    const rowNames: string[] = ["送信手段", "利用者グループ", "部屋名", "利用者名", "家族ユーザー名", "続柄", "役割", "家族からの連絡", "写真印刷依頼"];
    csv.push(`\ufeff` + `${rowNames.join(",")}`);

    for (const item of items) {
      const line: string[] = [];

      line.push(this.getSenderType(item.senderType));
      line.push(item.groupName);
      line.push(item.room);
      line.push(item.residentName);
      line.push(item.familyName);
      line.push(item.familyRelation);
      line.push(item.familyPosition ? item.familyPosition : "");

      if (item.senderType == SenderType.LINE) {
        line.push(item.familyReplyFunction ? "使用可" : "使用不可");
        line.push(item.familyPictureFunction ? "使用可" : "使用不可");
      }
      // メールユーザーは「家族からの連絡」「写真印刷依頼」を使用できないので「－」を出力する
      else {
        line.push("－");
        line.push("－");
      }

      csv.push(`${line.join(",")}`);
    }
    return csv.join("\n");
  }

  /**
   * ### 送信種別を返す
   */
  public getSenderType(senderType: SenderType): string {
    if (senderType == SenderType.LINE) {
      return "LINE";
    } else if (senderType == SenderType.MAIL) {
      return "メール";
    } else {
      return "";
    }
  }

  /**
   * ### CSVファイル名を作成する
   */
  public createCsvFileName(): string {
    const date: string = this.$dateUtil.convertDateToString(new Date(), "yyyymmdd");
    const fileName: string = `HitomeQコネクト-家族一覧-${date}.csv`;
    return fileName;
  }

  /**
   * ### CSVファイルをダウンロードさせる
   *
   * - CSVの内容からblobファイルを作成する
   * - blobファイルの種別はtext/csv
   * - a要素を作ってそれを疑似的にクリックさせることでダウンロードさせる
   */
  public downloadCsv(csv: string, fileName: string): void {
    const blob: Blob = new Blob([csv], { type: "text/csv" });
    let link = document.createElement("a");
    link.href = window.URL.createObjectURL(blob);
    link.download = fileName;
    link.click();
    return;
  }

  /**
   * ### 画面情報を再取得する
   */
  public async refresh(): Promise<void> {
    await this.initialize();
    return;
  }

  /**
   * ### vue lifecycle deactivated
   *
   * - コンポーネントをkeep-aliveでキャッシュしている
   * - validatorインスタンスもキャッシュしてしまっているので、リセットする
   */
  async deactivated() {
    await this.$validator.reset();
    this.$validator.errors.clear();

    // 画面を離れる前に現在のフィルタリング条件をVuexに保存
    if (this.$options.name) {
      itemListFilterStore.setFilter({ url: this.$options.name, filter: this.filter });
    }
    return;
  }
}
</script>

<style lang="scss" scoped>
@import "../../../styles/pc/_colors.scss";
@import "../../../styles/pc/_mixin.scss";

#family-list {
  max-width: 1280px;
  margin-left: auto;
  margin-right: auto;

  .box-header {
    .title {
      font-size: 22px;
      font-weight: bold;
    }
  }

  .box-body {
    .refresh-icon {
      @include svg-icon("../../../assets/refresh.svg", $font-base-color, 20px, 20px);
      opacity: 0.5;
      text-shadow: 0 1px 0 #fff;
      cursor: pointer;
    }
    .refresh-icon:hover {
      opacity: 0.75;
    }
  }
}
</style>
